import { z } from 'zod'
import {
  docId,
  isoTimestamp,
  money,
  noReceiptReasonEnum,
  reconciledViaEnum,
  sourceEnum,
  transactionStatusEnum,
} from './common.js'

/**
 * `transactions` — REQUIREMENTS.md §5.3. The ledger.
 *
 * One document per financial event. External-code transactions live here like
 * any other; the distinction lives on the linked budget code's `external` flag.
 *
 * Confirmation follows one of two paths depending on `source`:
 *   pcard                        → packet submission  (`reconciledVia: 'packet'`)
 *   amazon / po / reimbursement  → CSIU import match  (`reconciledVia: 'csiu'`)
 *
 * P-Card rows appear in the CSIU export but are skipped on import (§6.4), so a
 * P-Card transaction can never be confirmed by CSIU.
 */
export const transactionSchema = z
  .object({
    id: docId.optional(),
    userId: z.string().min(1),
    budgetCodeId: docId,
    /** Denormalized from the budget code for query efficiency. */
    fiscalYear: z.number().int().min(2000).max(2100),

    source: sourceEnum,
    /** CSIU TransactionNumber, card transaction ID, or email message ID. */
    sourceRef: z.string().trim().max(200).nullable().default(null),

    transactionDate: isoTimestamp,
    /**
     * Date the item was received — the log sheet's "Date Item Received" column
     * (§8.1). Defaults to `transactionDate`, which is correct for meals,
     * travel, and services; edited for goods that arrive weeks later.
     */
    dateReceived: isoTimestamp,

    amount: money,
    vendor: z.string().trim().min(1).max(200),
    /**
     * The merchant exactly as the credit card statement prints it, set when a
     * statement line is matched to this charge. §8.1's log sheet prints *this*
     * rather than `vendor` — Matt confirmed the packet must show what the
     * statement shows, since that is what the business office reconciles
     * against, and a tidied name is a name they have to go looking for.
     *
     * Null until a statement claims the transaction. Nothing else writes it.
     */
    statementVendorName: z.string().trim().max(200).nullable().default(null),
    description: z.string().trim().max(500).default(''),

    status: transactionStatusEnum.default('pending'),
    reconciledAt: isoTimestamp.nullable().default(null),
    reconciledVia: reconciledViaEnum.nullable().default(null),
    /** Only ever set when confirmed via CSIU. */
    csiuJournalNumber: z.string().trim().max(100).nullable().default(null),

    receiptIds: z.array(docId).default([]),
    /** Shared by sibling transactions when one receipt is split (§7.8). */
    splitGroupId: docId.nullable().default(null),
    /** Points at the original when this is a JV reversal or a refund credit. */
    reversalOfTransactionId: docId.nullable().default(null),

    notes: z.string().trim().max(2000).default(''),
    /**
     * Set only when the user has affirmatively acknowledged that no receipt
     * exists. Its presence — not the absence of `receiptIds` — is what marks a
     * deliberate exception rather than an unfinished link (§5.3, §7.2).
     */
    noReceiptReason: noReceiptReasonEnum.nullable().default(null),
    noReceiptExplanation: z.string().trim().max(500).nullable().default(null),

    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
  })
  .refine((t) => t.reconciledVia !== 'csiu' || t.source !== 'pcard', {
    message:
      'P-Card transactions cannot be confirmed via CSIU — those rows are skipped on import (§6.4). They are confirmed by packet submission.',
    path: ['reconciledVia'],
  })
  .refine((t) => t.csiuJournalNumber === null || t.reconciledVia === 'csiu', {
    message: 'A CSIU journal number implies confirmation via CSIU.',
    path: ['csiuJournalNumber'],
  })
  .refine((t) => t.status !== 'reconciled' || t.reconciledVia !== null, {
    message: 'A reconciled transaction must record which path confirmed it.',
    path: ['reconciledVia'],
  })
  .refine(
    (t) => t.noReceiptReason !== 'other_cash' || (t.noReceiptExplanation?.trim().length ?? 0) > 0,
    {
      message: 'Selecting "other cash" requires a short explanation.',
      path: ['noReceiptExplanation'],
    },
  )

export type Transaction = z.infer<typeof transactionSchema>

/**
 * Manual entry form (§6.5). `csiu` is deliberately absent from the selectable
 * sources — that value belongs to the importer alone.
 */
export const manualTransactionSourceEnum = z.enum([
  'manual',
  'pcard',
  'amazon',
  'po',
  'reimbursement',
])

export const newTransactionSchema = z
  .object({
    budgetCodeId: docId,
    transactionDate: isoTimestamp,
    /** Left blank in the UI, defaults to `transactionDate` on submit. */
    dateReceived: isoTimestamp.optional(),
    amount: money,
    vendor: z.string().trim().min(1, 'Vendor is required.').max(200),
    description: z.string().trim().max(500).default(''),
    source: manualTransactionSourceEnum.default('manual'),
    notes: z.string().trim().max(2000).default(''),
  })
  .transform((t) => ({ ...t, dateReceived: t.dateReceived ?? t.transactionDate }))

export type NewTransaction = z.infer<typeof newTransactionSchema>
